import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [hero, setHero] = useState(null);
  const [logo, setLogo] = useState(null);
  const [company, setCompany] = useState("XTRA-OMS");

  useEffect(() => { if (user) nav("/"); }, [user, nav]);

  useEffect(() => {
    axios.get(`${API}/settings/public`)
      .then((r) => {
        // Guard against truncated/placeholder hero data — real images are >1KB base64
        const h = r.data?.login_hero_data;
        if (h && typeof h === "string" && h.length > 200) setHero(h);
        if (r.data?.app_logo_data) setLogo(r.data.app_logo_data);
        if (r.data?.company_name) setCompany(r.data.company_name);
      })
      .catch(() => {});
  }, []);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] bg-slate-950">
      {/* Left: form panel */}
      <div className="flex items-center justify-center p-8 lg:p-14 bg-slate-950 text-slate-100">
        <form onSubmit={submit} className="w-full max-w-sm space-y-7" data-testid="login-form">
          <div className="flex items-center gap-2.5">
            {logo ? (
              <div className="w-10 h-10 rounded-lg bg-white/95 p-1 flex items-center justify-center shadow-lg shadow-slate-950/40 overflow-hidden" data-testid="login-logo">
                <img src={logo} alt={company} className="max-w-full max-h-full object-contain" />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-400 to-orange-600 text-slate-950 flex items-center justify-center font-black shadow-lg shadow-orange-900/30 tracking-tighter">X</div>
            )}
            <div className="leading-tight">
              <div className="heading font-bold tracking-tight text-[15px] text-white">{company}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-widest">Order Management</div>
            </div>
          </div>

          <div>
            <h1 className="heading text-4xl font-bold tracking-tight text-white">Sign in</h1>
            <p className="text-sm text-slate-400 mt-2">Authorised internal access only.</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-400">Email</Label>
              <Input id="email" data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                     className="h-11 bg-slate-900 border-slate-800 text-slate-100 placeholder:text-slate-600 focus-visible:ring-amber-500/40 focus-visible:border-amber-500/60"
                     placeholder="you@kaldan.co" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-400">Password</Label>
              <Input id="password" data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                     className="h-11 bg-slate-900 border-slate-800 text-slate-100 placeholder:text-slate-600 focus-visible:ring-amber-500/40 focus-visible:border-amber-500/60" />
            </div>
          </div>

          <Button type="submit" data-testid="login-submit" disabled={busy}
                  className="w-full h-11 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-slate-950 font-semibold shadow-lg shadow-orange-900/20 transition-all">
            {busy ? "Signing in…" : "Sign in"}
          </Button>

          <p className="text-[11px] text-slate-600 pt-2">
            Restricted environment. All sessions are logged for audit compliance.
          </p>
        </form>
      </div>

      {/* Right: brand panel */}
      <div className="hidden lg:block relative overflow-hidden">
        {hero ? (
          <>
            <img src={hero} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-br from-slate-950/92 via-slate-950/80 to-slate-950/95" />
          </>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900" data-testid="login-hero-fallback">
            <div className="absolute inset-0 bg-[radial-gradient(1200px_600px_at_70%_30%,rgba(251,146,60,0.22),transparent_60%),radial-gradient(900px_500px_at_20%_80%,rgba(56,189,248,0.10),transparent_50%)]" />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-10">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-600 text-slate-950 flex items-center justify-center text-4xl font-black shadow-2xl shadow-orange-900/40 tracking-tighter mb-6">X</div>
              <div className="heading text-[44px] font-black text-white tracking-tight leading-none">XTRA</div>
              <div className="text-[11px] uppercase tracking-[0.4em] text-amber-500/80 mt-3">Order Management System</div>
              <div className="text-sm text-slate-500 mt-6 max-w-md">Internal operations console for sales, production, dispatch and accounts.</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
